import { supabase } from '../lib/supabase';
import { getRedirectUrl } from '../lib/deepLinks';
import { sendInviteEmail } from './emailService';

/**
 * Invite Service
 * Comprehensive service for managing user invites with profile pre-population
 */

/**
 * Creates a new user invite with optional pre-filled profile data.
 * Only Super Admins and Coaches can create invites.
 *
 * @param {string} email - The email address to invite
 * @param {object} preFilledData - Optional profile data to pre-populate (role, first_name, last_name, full_name, coach_id, avatar_url)
 * @param {number} expiresInDays - Number of days until the invite expires (default: 7)
 * @returns {Promise<{data: object, error: string}>} A promise that resolves to the created invite data
 */
export const createInvite = async (email, preFilledData = null, expiresInDays = 7) => {
  try {
    // Step 1: Create the invite in the database
    const { data, error } = await supabase.rpc('create_user_invite', {
      p_email: email,
      p_pre_filled_data: preFilledData,
      p_expires_in_days: expiresInDays
    });

    if (error) {
      console.error('Error creating invite:', error.message);
      return { data: null, error: `Failed to create invite: ${error.message}` };
    }

    if (!data) {
      return { data: null, error: 'Failed to create invite: No data returned' };
    }

    // Step 2: Get the current user's information for the email
    let inviterName = 'Your administrator';
    try {
      const { data: currentUser } = await supabase.auth.getUser();
      if (currentUser?.user?.id) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('first_name, last_name, full_name')
          .eq('id', currentUser.user.id)
          .single();

        if (profile) {
          if (profile.first_name && profile.last_name) {
            inviterName = `${profile.first_name} ${profile.last_name}`;
          } else if (profile.full_name) {
            inviterName = profile.full_name;
          }
        }
      }
    } catch (profileError) {
      console.warn('Could not fetch inviter profile:', profileError.message);
      // Continue with default name
    }

    // Step 3: Send the invitation email
    try {
      console.log('Sending invitation email to:', email);
      
      const emailResult = await sendInviteEmail(
        email,
        data.invite_token,
        data,
        inviterName
      );

      if (emailResult.success) {
        console.log('Invitation email sent successfully:', emailResult.messageId);
        
        // Log the email send action in the audit log
        try {
          await supabase
            .from('invite_audit_log')
            .insert({
              invite_id: data.id,
              action: 'sent',
              performed_by: (await supabase.auth.getUser()).data.user?.id,
              details: {
                email_provider: emailResult.provider,
                message_id: emailResult.messageId,
                sent_at: new Date().toISOString()
              }
            });
        } catch (auditError) {
          console.warn('Failed to log email send action:', auditError.message);
        }

        return {
          data: {
            ...data,
            email_sent: true,
            email_result: emailResult
          },
          error: null
        };
      } else {
        // Email failed but invite was created
        console.error('Failed to send invitation email:', emailResult.error);
        
        // Log the email failure
        try {
          await supabase
            .from('invite_audit_log')
            .insert({
              invite_id: data.id,
              action: 'email_failed',
              performed_by: (await supabase.auth.getUser()).data.user?.id,
              details: {
                error: emailResult.error,
                failed_at: new Date().toISOString()
              }
            });
        } catch (auditError) {
          console.warn('Failed to log email failure:', auditError.message);
        }

        return {
          data: {
            ...data,
            email_sent: false,
            email_error: emailResult.error
          },
          error: `Invite created but email failed: ${emailResult.error}`
        };
      }
    } catch (emailError) {
      console.error('Unexpected error sending email:', emailError);
      return {
        data: {
          ...data,
          email_sent: false,
          email_error: emailError.message
        },
        error: `Invite created but email failed: ${emailError.message}`
      };
    }

  } catch (err) {
    console.error('Error in createInvite:', err.message);
    return { data: null, error: `Failed to create invite: ${err.message}` };
  }
};

/**
 * Validates an invite token and returns invite details if valid.
 * Can be called by anyone (including anonymous users during registration).
 *
 * @param {string} token - The invite token to validate
 * @returns {Promise<{data: object, error: string}>} A promise that resolves to the invite validation result
 */
export const validateInviteToken = async (token) => {
  try {
    // DEBUG: Log token details for diagnosis
    console.log('🔍 DEBUGGING: Token validation details:', {
      originalToken: token,
      tokenLength: token?.length,
      hasPlus: token?.includes('+'),
      hasSlash: token?.includes('/'),
      hasEquals: token?.includes('='),
      hasSpace: token?.includes(' '),
      urlDecoded: decodeURIComponent(token),
      urlDecodedLength: decodeURIComponent(token)?.length,
      base64Pattern: /^[A-Za-z0-9+/]*={0,2}$/.test(token),
      timestamp: new Date().toISOString()
    });

    const { data, error } = await supabase.rpc('validate_invite_token', {
      p_token: token
    });

    if (error) {
      console.error('❌ Database validation error:', error.message);
      console.error('Token that failed:', { token, tokenType: typeof token });
      return { data: null, error: `Failed to validate invite: ${error.message}` };
    }

    // DEBUG: Log validation result
    if (data && !data.valid) {
      console.error('❌ Token validation failed:', {
        token: token,
        response: data,
        error: data.error
      });
    } else if (data && data.valid) {
      console.log('✅ Token validation succeeded:', {
        tokenPrefix: token?.substring(0, 8) + '...',
        responseValid: data.valid
      });
    }

    return { data, error: null };
  } catch (err) {
    console.error('❌ Error in validateInviteToken:', err.message);
    console.error('Token details on error:', { token, error: err });
    return { data: null, error: `Failed to validate invite: ${err.message}` };
  }
};

/**
 * Claims an invite and applies pre-filled data to user profile.
 * Called after successful user registration to complete the invite flow.
 *
 * @param {string} token - The invite token to claim
 * @param {string} userId - The newly created user's ID
 * @param {object} additionalProfileData - Any additional profile data from registration form
 * @returns {Promise<{data: object, error: string}>} A promise that resolves to the claim result
 */
export const claimInvite = async (token, userId, additionalProfileData = null) => {
  try {
    const { data, error } = await supabase.rpc('claim_invite', {
      p_token: token,
      p_user_id: userId,
      p_additional_profile_data: additionalProfileData
    });

    if (error) {
      console.error('Error claiming invite:', error.message);
      return { data: null, error: `Failed to claim invite: ${error.message}` };
    }

    return { data, error: null };
  } catch (err) {
    console.error('Error in claimInvite:', err.message);
    return { data: null, error: `Failed to claim invite: ${err.message}` };
  }
};

/**
 * Revokes an active invite.
 * Only Super Admins and Coaches (for their own invites) can revoke invites.
 *
 * @param {string} inviteId - The ID of the invite to revoke
 * @param {string} reason - Optional reason for revoking the invite
 * @returns {Promise<{data: object, error: string}>} A promise that resolves to the revoke result
 */
export const revokeInvite = async (inviteId, reason = null) => {
  try {
    const { data, error } = await supabase.rpc('revoke_invite', {
      p_invite_id: inviteId,
      p_reason: reason
    });

    if (error) {
      console.error('Error revoking invite:', error.message);
      return { data: null, error: `Failed to revoke invite: ${error.message}` };
    }

    return { data, error: null };
  } catch (err) {
    console.error('Error in revokeInvite:', err.message);
    return { data: null, error: `Failed to revoke invite: ${err.message}` };
  }
};

/**
 * Fetches paginated list of invites for admin dashboard.
 * Super Admins can see all invites, Coaches can see their own invites.
 *
 * @param {string} currentUserId - The ID of the current user (not used in new function, handled by RLS)
 * @param {string} currentUserRole - The role of the current user (not used in new function, handled by RLS)
 * @param {object} options - Query options (page, pageSize, search, statusFilter, sortBy, sortOrder)
 * @returns {Promise<{data: Array, error: string, count: number, page: number, pageSize: number, totalPages: number}>}
 */
export const getInvites = async (currentUserId, currentUserRole, options = {}) => {
  try {
    const {
      page = 1,
      pageSize = 10,
      search = '',
      statusFilter = '',
      sortBy = 'created_at',
      sortOrder = 'desc'
    } = options;

    // Use the updated RPC function with timeout protection
    const queryTimeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Query timeout')), 10000)
    );

    const rpcQuery = supabase.rpc('get_user_invites_paginated', {
      p_search_term: search,
      p_status_filter: statusFilter,
      p_sort_by: sortBy,
      p_sort_order: sortOrder,
      p_page_offset: (page - 1) * pageSize,
      p_page_limit: pageSize
    });

    const { data: rpcData, error: rpcError } = await Promise.race([rpcQuery, queryTimeout]);

    if (rpcError) {
      console.error('Error fetching invites:', rpcError.message);
      return { data: null, error: `Failed to fetch invites: ${rpcError.message}`, count: 0 };
    }

    if (rpcData) {
      const invites = rpcData.invites || [];
      const totalCount = rpcData.total_count || 0;
      
      return {
        data: invites,
        error: null,
        count: totalCount,
        page,
        pageSize,
        totalPages: Math.ceil(totalCount / pageSize)
      };
    }

    // Return empty result if no data
    return {
      data: [],
      error: null,
      count: 0,
      page,
      pageSize,
      totalPages: 0
    };
  } catch (err) {
    console.error('Error in getInvites:', err.message);
    return { data: null, error: `Failed to fetch invites: ${err.message}`, count: 0 };
  }
};

/**
 * Fetches a single invite by ID.
 * Respects RLS policies - users can only see invites they have access to.
 *
 * @param {string} inviteId - The ID of the invite to fetch
 * @returns {Promise<{data: object, error: string}>} A promise that resolves to the invite data
 */
export const getInviteById = async (inviteId) => {
  try {
    const { data, error } = await supabase
      .from('user_invites')
      .select(`
        id,
        invited_by,
        email,
        invite_token,
        expires_at,
        claimed_at,
        pre_filled_data,
        status,
        created_at,
        updated_at,
        revoked_at,
        revoked_by,
        revoke_reason
      `)
      .eq('id', inviteId)
      .single();

    if (error) {
      console.error('Error fetching invite:', error.message);
      return { data: null, error: `Failed to fetch invite: ${error.message}` };
    }

    return { data, error: null };
  } catch (err) {
    console.error('Error in getInviteById:', err.message);
    return { data: null, error: `Failed to fetch invite: ${err.message}` };
  }
};

/**
 * Resends an invite by creating a new token for the same email.
 * The old invite is revoked and a new one is created.
 *
 * @param {string} inviteId - The ID of the invite to resend
 * @param {number} expiresInDays - Number of days until the new invite expires (default: 7)
 * @returns {Promise<{data: object, error: string}>} A promise that resolves to the new invite data
 */
export const resendInvite = async (inviteId, expiresInDays = 7) => {
  try {
    // First get the original invite details
    const { data: originalInvite, error: fetchError } = await getInviteById(inviteId);
    
    if (fetchError || !originalInvite) {
      return { data: null, error: fetchError || 'Invite not found' };
    }

    // Check if invite can be resent (must be pending or expired)
    if (originalInvite.status === 'claimed') {
      return { data: null, error: 'Cannot resend a claimed invite' };
    }

    if (originalInvite.status === 'revoked') {
      return { data: null, error: 'Cannot resend a revoked invite' };
    }

    // Revoke the old invite
    const { error: revokeError } = await revokeInvite(inviteId, 'Resent with new token');
    
    if (revokeError) {
      console.warn('Failed to revoke old invite:', revokeError);
      // Continue anyway as we can still create a new invite
    }

    // Create a new invite with the same data (this will automatically send email)
    const { data: newInvite, error: createError } = await createInvite(
      originalInvite.email,
      originalInvite.pre_filled_data,
      expiresInDays
    );

    if (createError) {
      return { data: null, error: createError };
    }

    // Log the resend action (in addition to the logs created by createInvite)
    try {
      await supabase
        .from('invite_audit_log')
        .insert({
          invite_id: newInvite.id,
          action: 'resent',
          performed_by: (await supabase.auth.getUser()).data.user?.id,
          details: {
            original_invite_id: inviteId,
            resent_at: new Date().toISOString(),
            email_sent: newInvite.email_sent || false
          }
        });
    } catch (logError) {
      console.warn('Failed to log resend action:', logError.message);
    }

    return {
      data: newInvite,
      error: newInvite.email_sent === false ? newInvite.email_error : null
    };
  } catch (err) {
    console.error('Error in resendInvite:', err.message);
    return { data: null, error: `Failed to resend invite: ${err.message}` };
  }
};

/**
 * Gets invite status counts for dashboard overview.
 * Returns count of pending, claimed, expired, and revoked invites.
 *
 * @param {string} currentUserId - The ID of the current user
 * @param {string} currentUserRole - The role of the current user ('SUPER_ADMIN', 'COACH')
 * @returns {Promise<{data: object, error: string}>} A promise that resolves to invite counts
 */
export const getInviteStatusCounts = async (currentUserId, currentUserRole) => {
  try {
    let query = supabase.from('user_invites').select('status', { count: 'exact', head: true });
    
    // Apply role-based filtering
    if (currentUserRole === 'COACH') {
      query = query.eq('invited_by', currentUserId);
    }
    // Super admins can see all

    // Get counts for each status
    const statusCounts = {};
    const statuses = ['pending', 'claimed', 'expired', 'revoked'];
    
    for (const status of statuses) {
      try {
        let statusQuery = supabase.from('user_invites')
          .select('*', { count: 'exact', head: true })
          .eq('status', status);
        
        if (currentUserRole === 'COACH') {
          statusQuery = statusQuery.eq('invited_by', currentUserId);
        }
        
        const { count, error } = await statusQuery;
        if (error) throw error;
        
        statusCounts[status] = count || 0;
      } catch (statusError) {
        console.warn(`Failed to get ${status} count:`, statusError.message);
        statusCounts[status] = 0;
      }
    }

    // Calculate total
    statusCounts.total = Object.values(statusCounts).reduce((sum, count) => sum + count, 0);

    return { data: statusCounts, error: null };
  } catch (err) {
    console.error('Error in getInviteStatusCounts:', err.message);
    return { 
      data: { pending: 0, claimed: 0, expired: 0, revoked: 0, total: 0 }, 
      error: `Failed to fetch invite counts: ${err.message}` 
    };
  }
};

/**
 * Cleans up expired invites by updating their status.
 * This function can be called periodically or manually by admins.
 *
 * @returns {Promise<{data: number, error: string}>} A promise that resolves to the number of invites updated
 */
export const cleanupExpiredInvites = async () => {
  try {
    const { data, error } = await supabase.rpc('cleanup_expired_invites');

    if (error) {
      console.error('Error cleaning up expired invites:', error.message);
      return { data: 0, error: `Failed to cleanup expired invites: ${error.message}` };
    }

    return { data: data || 0, error: null };
  } catch (err) {
    console.error('Error in cleanupExpiredInvites:', err.message);
    return { data: 0, error: `Failed to cleanup expired invites: ${err.message}` };
  }
};

/**
 * Cleans up old invites by permanently removing expired and revoked invites.
 * This function can be called manually by admins to clean up the database.
 *
 * @returns {Promise<{data: number, error: string}>} A promise that resolves to the number of invites removed
 */
export const cleanupOldInvites = async () => {
  try {
    // Delete expired invites
    const { data: expiredData, error: expiredError } = await supabase
      .from('user_invites')
      .delete()
      .eq('status', 'expired')
      .select('id');

    if (expiredError) {
      console.error('Error deleting expired invites:', expiredError.message);
      return { data: 0, error: `Failed to delete expired invites: ${expiredError.message}` };
    }

    // Delete revoked invites
    const { data: revokedData, error: revokedError } = await supabase
      .from('user_invites')
      .delete()
      .eq('status', 'revoked')
      .select('id');

    if (revokedError) {
      console.error('Error deleting revoked invites:', revokedError.message);
      return { data: 0, error: `Failed to delete revoked invites: ${revokedError.message}` };
    }

    const totalDeleted = (expiredData?.length || 0) + (revokedData?.length || 0);
    
    console.log(`Cleaned up ${totalDeleted} old invites`, {
      expired: expiredData?.length || 0,
      revoked: revokedData?.length || 0
    });

    return { data: totalDeleted, error: null };
  } catch (err) {
    console.error('Error in cleanupOldInvites:', err.message);
    return { data: 0, error: `Failed to cleanup old invites: ${err.message}` };
  }
};

/**
 * Gets audit log for a specific invite.
 * Shows the complete history of actions performed on the invite.
 *
 * @param {string} inviteId - The ID of the invite
 * @returns {Promise<{data: Array, error: string}>} A promise that resolves to the audit log entries
 */
export const getInviteAuditLog = async (inviteId) => {
  try {
    console.log('🔍 Fetching audit log for invite:', inviteId);
    
    // First, get the basic audit log entries
    const { data: auditEntries, error } = await supabase
      .from('invite_audit_log')
      .select(`
        id,
        action,
        details,
        created_at,
        ip_address,
        user_agent,
        performed_by
      `)
      .eq('invite_id', inviteId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Error fetching basic audit log:', error.message);
      return { data: [], error: `Failed to fetch audit log: ${error.message}` };
    }

    // Now get user details for non-null performed_by values
    const data = [];
    for (const entry of auditEntries || []) {
      let userProfile = null;
      
      if (entry.performed_by) {
        try {
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name, full_name, email')
            .eq('id', entry.performed_by)
            .single();
          userProfile = profile;
        } catch (profileError) {
          console.warn('Could not fetch user profile for audit entry:', profileError);
        }
      }
      
      data.push({
        ...entry,
        profiles: userProfile
      });
    }

    console.log('🔍 Audit log query result:', { data, error, recordCount: data?.length });
    console.log('✅ Audit log fetched successfully:', data?.length || 0, 'records');
    return { data: data || [], error: null };
  } catch (err) {
    console.error('❌ Error in getInviteAuditLog:', err.message);
    return { data: [], error: `Failed to fetch audit log: ${err.message}` };
  }
};

/**
 * Formats invite status for display.
 *
 * @param {string} status - The invite status
 * @returns {object} Formatted status with display properties
 */
export const formatInviteStatus = (status) => {
  switch (status) {
    case 'pending':
      return {
        label: 'Pending',
        color: 'yellow',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-200'
      };
    case 'claimed':
      return {
        label: 'Claimed',
        color: 'green',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-200'
      };
    case 'expired':
      return {
        label: 'Expired',
        color: 'gray',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        borderColor: 'border-gray-200'
      };
    case 'revoked':
      return {
        label: 'Revoked',
        color: 'red',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-200'
      };
    default:
      return {
        label: status,
        color: 'gray',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        borderColor: 'border-gray-200'
      };
  }
};

/**
 * Formats invite expiration time for display.
 *
 * @param {string} expiresAt - ISO timestamp when invite expires
 * @returns {object} Formatted expiration info
 */
export const formatInviteExpiration = (expiresAt) => {
  if (!expiresAt) return { text: 'No expiration', isExpired: false, isExpiringSoon: false };
  
  const expireDate = new Date(expiresAt);
  const now = new Date();
  const diffMs = expireDate - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  
  const isExpired = diffMs <= 0;
  const isExpiringSoon = diffDays <= 1 && diffDays > 0;
  
  if (isExpired) {
    return { text: 'Expired', isExpired: true, isExpiringSoon: false };
  }
  
  if (diffDays === 0) {
    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
    return { 
      text: `Expires in ${diffHours} hour${diffHours === 1 ? '' : 's'}`, 
      isExpired: false, 
      isExpiringSoon: true 
    };
  }
  
  return { 
    text: `Expires in ${diffDays} day${diffDays === 1 ? '' : 's'}`, 
    isExpired: false, 
    isExpiringSoon 
  };
};

/**
 * Generates invite URL for sharing.
 *
 * @param {string} inviteToken - The invite token
 * @returns {string} Complete invite URL
 */
export const generateInviteUrl = (inviteToken) => {
  return getRedirectUrl(`/register?invite=${inviteToken}`);
};

/**
 * Validates email format for invites.
 *
 * @param {string} email - Email to validate
 * @returns {boolean} True if email is valid
 */
export const validateEmail = (email) => {
  const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
  return emailRegex.test(email);
};

/**
 * Validates pre-filled profile data structure.
 *
 * @param {object} preFilledData - Profile data to validate
 * @returns {object} Validation result with any errors
 */
export const validatePreFilledData = (preFilledData) => {
  if (!preFilledData) return { valid: true, errors: [] };
  
  const errors = [];
  
  // Validate role if provided
  if (preFilledData.role && !['SUPER_ADMIN', 'COACH', 'CLIENT'].includes(preFilledData.role)) {
    errors.push('Invalid role. Must be SUPER_ADMIN, COACH, or CLIENT.');
  }
  
  // Validate coach_id if provided (should be UUID format)
  if (preFilledData.coach_id) {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(preFilledData.coach_id)) {
      errors.push('Invalid coach ID format.');
    }
  }
  
  // Validate name fields length
  if (preFilledData.first_name && preFilledData.first_name.length > 100) {
    errors.push('First name is too long (max 100 characters).');
  }
  
  if (preFilledData.last_name && preFilledData.last_name.length > 100) {
    errors.push('Last name is too long (max 100 characters).');
  }
  
  if (preFilledData.full_name && preFilledData.full_name.length > 200) {
    errors.push('Full name is too long (max 200 characters).');
  }
  
  return { valid: errors.length === 0, errors };
};